import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { spokeIn, spokeState, type BattleState, type BookSealRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** The page in the hand: a leaf with a burning edge. */
const PAGE =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2h6l3 3v9H4z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M6 7h5M6 9.5h5M6 12h3" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** Fallen beams over a stair. */
const BEAMS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 11l12-5M2 6l12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
/** A seal. */
const SEAL =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.4" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.6v6.8M4.6 8h6.8" stroke="currentColor" stroke-width="1.2"/></svg>';
/** A quill (the scribes). */
const QUILL =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.5 2.5c-5 1-8 5-9.5 10.5l1 1C10 12 13 8 13.5 2.5z" fill="currentColor"/><path d="M3 14.5l2-2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** Fire. */
const FIRE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c1 3 4 4 4 8a4 4 0 0 1-8 0c0-2 1-3 2-4 0 1.5.6 2.4 1.4 2.6C7 6 7.2 3.8 8 1.5z" fill="currentColor"/></svg>';

/** A frost bowl (a snowflake), an acid flask (a drop), a brazier (a flame). */
const ITEM_ICON = [
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M2.4 4.8l11.2 6.4M2.4 11.2l11.2-6.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.8c2.2 3.4 4.2 5.6 4.2 8.2a4.2 4.2 0 0 1-8.4 0c0-2.6 2-4.8 4.2-8.2z" fill="currentColor"/></svg>',
  FIRE,
] as const;
const SEAL_KEY = ['frost', 'crust', 'ink'] as const;
const ITEM_KEY = ['bowl', 'flask', 'brazier'] as const;

type Mark = {
  el: HTMLButtonElement;
  state: HTMLElement;
  at: number;
  point: THREE.Vector3;
};

/**
 * The burnt library on screen (docs/sendas/mago.md, chapter 1), inside the path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the cellars (`soot`): the hall of four and how many of its risen stand; the beams over its stair (down while one
 *   stands) or the catalog below; and the page in the hand, still, throbbing or burning by how near something is.
 * - the locks and the cabinet (`seals`): each seal's state in a pip of its colour (sealed, readied, broken); the scribe
 *   coming, waiting at the desk or writing (its count red); in the test, the seals sealed again of three and the next
 *   wall of fire. Over the board a mark on each seal (its kind and its state) and each thing by it (its name; gone, when
 *   it comes back). A tap on a mark sends the hero to bolt it. The words are names and states: what readies what, the
 *   player reads off the colours and the seals' answers.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class BiblioHud {
  readonly panel: HTMLElement;
  private kind: 'soot' | 'seals';
  private v = new THREE.Vector3();
  private key = '';
  private hallRow = h('div.bh-row.bb-hall');
  private hallText = h('span.al-wave');
  private beamRow = h('div.bh-row.al-waverow.bb-beams');
  private beamText = h('span.al-wave');
  private glowRow = h('div.bh-row.bb-glow');
  private glowText = h('span.al-wave');
  private sealRow = h('div.bh-row.bb-seals');
  private sealPips: HTMLElement[] = [];
  private scribeRow = h('div.bh-row.al-waverow.bb-scribe');
  private scribeText = h('span.al-wave');
  private fireRow = h('div.bh-row.al-waverow.bb-fire');
  private fireText = h('span.al-wave');
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.seals ? 'seals' : 'soot';
    const rows: HTMLElement[] = [];
    if (this.kind === 'soot') {
      this.hallRow.append(h('span.bh-ico', { html: PAGE }), this.hallText);
      this.beamRow.append(h('span.bh-ico', { html: BEAMS }), this.beamText);
      this.glowRow.append(h('span.bh-ico', { html: PAGE }), this.glowText);
      rows.push(this.hallRow, this.beamRow, this.glowRow);
    } else if (sr?.seals) {
      const r = sr.seals;
      this.sealPips = r.seals.map((x) =>
        h(`i.bb-pip.k${x.kind}`, {
          title: t(`senda.biblio.${SEAL_KEY[x.kind]}`),
        }),
      );
      this.sealRow.append(h('span.bh-ico', { html: SEAL }), h('span.bh-label', {}, t('senda.biblio.seals')), h('span.al-pips.bb-pips', {}, ...this.sealPips));
      this.scribeRow.append(h('span.bh-ico', { html: QUILL }), this.scribeText);
      rows.push(this.sealRow, this.scribeRow);
      if (r.spokes) {
        this.fireRow.append(h('span.bh-ico', { html: FIRE }), this.fireText);
        rows.push(this.fireRow);
      }
      this.buildMarks(r);
    }
    this.panel = h(`div.bh.bb-panel.bb-${this.kind}`, {}, ...rows);
  }

  /** A mark on each thing by a seal and on each seal. */
  private buildMarks(r: BookSealRules): void {
    const n = r.items.length + r.seals.length;
    for (let at = 0; at < n; at++) {
      const item = at < r.items.length;
      const p = item ? (r.items[at] as BookSealRules['items'][number]).at : (r.seals[at - r.items.length] as BookSealRules['seals'][number]).at;
      const kind = item ? (r.items[at] as BookSealRules['items'][number]).kind : (r.seals[at - r.items.length] as BookSealRules['seals'][number]).kind;
      const name = item ? t(`senda.biblio.${ITEM_KEY[kind]}`) : t(`senda.biblio.${SEAL_KEY[kind]}`);
      const hint = t(item ? 'senda.biblio.itemHint' : 'senda.biblio.sealHint');
      const state = h('small.al-state.bb-state');
      const el = h(
        `button.al-stone.bb-mark${item ? `.item.i${kind}` : `.seal.k${kind}`}`,
        {
          type: 'button',
          'data-test': item ? `biblio-item-${at}` : `biblio-seal-${at - r.items.length}`,
          'aria-label': `${name}: ${hint}`,
          title: hint,
          onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)),
        },
        h('span.al-ring'),
        // (A thing's mark shows what it is by its glyph: three names side by side ran into each other on a phone.)
        item ? h('span.al-glyph', { html: ITEM_ICON[kind] }) : h('b.mn-name.bb-name', {}, name),
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      // (The seals stand on their lecterns or the cabinet's faces: the mark at their height.)
      this.marks.push({
        el,
        state,
        at,
        point: simToWorld(p.x, p.y, new THREE.Vector3(), item ? 0.7 : 1.3),
      });
    }
  }

  private updateSoot(s: BattleState): void {
    const r = s.rules?.senda?.soot;
    const w = s.senda?.soot;
    if (!r || !w) return;
    let up = 0;
    for (const id of w.risen[w.hall] ?? []) if (id && s.enemies.some((e) => e.id === id && e.hp > 0)) up++;
    const hall = t('senda.biblio.hall', { n: w.hall + 1, of: r.halls.length }) + (up ? ` · ${t('senda.biblio.risen', { n: up })}` : '');
    if (this.hallText.textContent !== hall) this.hallText.textContent = hall;
    const shut = !!w.shut[w.hall];
    const bt = shut ? t('senda.biblio.beamsDown') : w.hall >= r.halls.length - 1 ? t('senda.biblio.catalog') : t('senda.biblio.beamsUp');
    if (this.beamText.textContent !== bt) this.beamText.textContent = bt;
    this.beamRow.classList.toggle('warn', shut);
    const near = w.near > 0 && w.near < 2600;
    const gt = !w.near ? t('senda.biblio.glowNone') : near ? t('senda.biblio.glowNear') : t('senda.biblio.glowFar');
    if (this.glowText.textContent !== gt) this.glowText.textContent = gt;
    this.glowRow.classList.toggle('warn', near);
    this.glowRow.classList.toggle('lit', w.near > 0);
  }

  private updateSeals(s: BattleState): void {
    const r = s.rules?.senda?.seals;
    const w = s.senda?.seals;
    if (!r || !w) return;
    w.seals.forEach((x, i) => {
      const p = this.sealPips[i];
      if (!p) return;
      p.classList.toggle('on', x.broken);
      p.classList.toggle('ready', !x.broken && x.prep > 0);
    });
    // The scribe: writing (its count), waiting at the desk, or on its way.
    let txt = '';
    let warn = false;
    for (const u of w.scribes) {
      if (u.mode === 1) {
        txt = t('senda.biblio.scribeWriting', {
          s: Math.max(1, Math.ceil((u.t - s.tick) / TICK_HZ)),
        });
        warn = true;
        break;
      }
      if (u.mode === 2) txt = t('senda.biblio.scribeWaits');
      else if (!txt) txt = t('senda.biblio.scribeComing');
    }
    if (r.lose) txt = `${txt ? `${txt} · ` : ''}${t('senda.biblio.resealed', { n: w.resealed, of: r.lose })}`;
    this.scribeRow.hidden = !txt;
    if (this.scribeText.textContent !== txt) this.scribeText.textContent = txt;
    this.scribeRow.classList.toggle('warn', warn);
    // The next wall of fire.
    const sp = r.spokes;
    if (sp) {
      let next = 0x7fffffff;
      let upNow = false;
      for (let k = 0; k < sp.cells.length; k++) {
        if (spokeState(sp, k, s.waveT) === 2) upNow = true;
        const n = spokeIn(sp, k, s.waveT);
        if (n > 0) next = Math.min(next, n);
      }
      const ft = t('senda.biblio.fireIn', {
        s: Math.max(1, Math.ceil(next / TICK_HZ)),
      });
      if (this.fireText.textContent !== ft) this.fireText.textContent = ft;
      this.fireRow.classList.toggle('warn', next <= sp.warn || upNow);
    }
    // The marks: a seal's state, a thing gone and when it's back.
    const key = `${Math.floor(s.tick / TICK_HZ)}|${w.seals.map((x) => `${x.broken ? 'B' : x.prep}`).join('')}|${w.back.map((b) => (b ? Math.ceil((b - s.tick) / TICK_HZ) : 0)).join(',')}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        let st: string;
        if (m.at < r.items.length) {
          const b = w.back[m.at] ?? 0;
          st = b
            ? t('senda.biblio.itemBack', {
                s: Math.max(1, Math.ceil((b - s.tick) / TICK_HZ)),
              })
            : '';
          m.el.classList.toggle('away', !!b);
        } else {
          const x = w.seals[m.at - r.items.length];
          st = !x ? '' : x.broken ? (r.hold && w.at ? t('senda.biblio.closesIn', { s: Math.max(1, Math.ceil(((w.at[m.at - r.items.length] ?? 0) + r.hold - s.tick) / TICK_HZ)) }) : t('senda.biblio.broken')) : x.prep === 1 ? t('senda.biblio.readyFrost') : x.prep === 2 ? t('senda.biblio.readyAcid') : t('senda.biblio.whole');
          m.el.classList.toggle('done', !!x?.broken);
          m.el.classList.toggle('ready', !!x && !x.broken && x.prep > 0);
        }
        if (m.state.textContent !== st) {
          m.state.textContent = st;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('sent', w.job === m.at);
      }
    }
    for (const m of this.marks) m.el.disabled = s.result !== 'running' || w.done >= 0;
  }

  update(s: BattleState): void {
    if (this.kind === 'soot') this.updateSoot(s);
    else this.updateSeals(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
